// packages/frontend/src/features/recipes/RecipeRow.tsx
//
// List A — two-line list row. 80px square thumbnail + title + description
// snippet + meta + rating pill. Used in the library when the user has chosen
// the "List" view via the toolbar toggle.
//
// Layout:
//   [thumb] Title                          [rating]
//           Description snippet (1 line)
//           45m · easy · 4 srv · [chip][chip]
import type { JSX } from "react";
import { Link } from "react-router";
import type { RecipeApiItem, TaxonomyApiItem } from "@recipevault/shared";
import { RecipePhotoImg } from "./RecipePhotoImg.js";
import { CoverPlaceholder } from "./CoverPlaceholder.js";
import Rating from "./Rating.js";
import { KIND_CHIP_CLASS } from "./taxonomyKindStyle.js";
import { descSnippet } from "./descSnippet.js";

function formatMin(min: number): string {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** Compose the meta line: "45m · easy · 4 srv · cooked N×". Skips null fields. */
function metaParts(recipe: RecipeApiItem): string[] {
  const parts: string[] = [];
  if (recipe.totalTimeMin != null) parts.push(formatMin(recipe.totalTimeMin));
  if (recipe.difficulty != null) parts.push(recipe.difficulty);
  if (recipe.servings != null) parts.push(`${recipe.servings} srv`);
  if (recipe.stats != null && recipe.stats.cookCount > 0)
    parts.push(`cooked ${recipe.stats.cookCount}×`);
  return parts;
}

/**
 * Same chip-picker policy as the grid card: cuisine → first category → first
 * tag → first diet, capped at 2. Single source of truth would be nicer but the
 * pick is simple and duplicating it here avoids an inter-component dependency.
 */
function pickChips(
  recipe: RecipeApiItem,
  taxonomy: Map<string, TaxonomyApiItem>,
  max = 2,
): TaxonomyApiItem[] {
  const ordered: (string | null | undefined)[] = [
    recipe.taxonomy.cuisineId,
    recipe.taxonomy.categoryIds[0],
    recipe.taxonomy.tagIds[0],
    recipe.taxonomy.dietFlags[0],
  ];
  const picked: TaxonomyApiItem[] = [];
  for (const id of ordered) {
    if (!id) continue;
    const t = taxonomy.get(id);
    if (t && !t.deletedAt) picked.push(t);
    if (picked.length >= max) break;
  }
  return picked;
}

interface Props {
  recipe: RecipeApiItem;
  householdId: string;
  taxonomy: Map<string, TaxonomyApiItem>;
}

export function RecipeRow({ recipe, householdId, taxonomy }: Props): JSX.Element {
  const chips = pickChips(recipe, taxonomy);
  const parts = metaParts(recipe);
  const snippet = descSnippet(recipe.description);

  return (
    <Link
      to={`/recipes/${recipe.id}`}
      className="group grid grid-cols-[80px_1fr] sm:grid-cols-[80px_1fr_auto] gap-3 sm:gap-4 items-center px-3 py-3 bg-zinc-900 border border-zinc-800 hover:border-emerald-700/50 hover:bg-zinc-900/60 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50"
      data-testid="recipe-row"
    >
      {recipe.photos.coverKey ? (
        <RecipePhotoImg
          householdId={householdId}
          recipeId={recipe.id}
          photoKey={recipe.photos.coverKey}
          size="thumb"
          alt={`Cover for ${recipe.title}`}
          className="w-20 h-20 rounded-md object-cover shrink-0"
        />
      ) : (
        <CoverPlaceholder
          seed={recipe.id}
          title={recipe.title}
          className="w-20 h-20 rounded-md shrink-0"
          letterClassName="text-2xl"
        />
      )}

      <div className="min-w-0 flex flex-col gap-0.5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-zinc-50 font-medium text-base leading-tight truncate">{recipe.title}</h3>
          {/* Mobile-only inline rating — desktop hides this and uses the right column instead. */}
          {recipe.rating != null && (
            <div className="sm:hidden shrink-0">
              <Rating value={recipe.rating} size="xs" />
            </div>
          )}
        </div>
        {snippet !== null && (
          <p className="text-zinc-400 text-sm truncate" data-testid="recipe-row-desc">
            {snippet}
          </p>
        )}
        <div className="flex items-center gap-2 flex-wrap text-xs text-zinc-500" data-testid="recipe-row-meta">
          {parts.length > 0 && (
            <span>{parts.join(" · ")}</span>
          )}
          {chips.length > 0 && parts.length > 0 && <span aria-hidden="true">·</span>}
          {chips.map((c) => (
            <span
              key={c.id}
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[0.625rem] ${KIND_CHIP_CLASS[c.kind]}`}
            >
              {c.name}
            </span>
          ))}
        </div>
      </div>

      {/* Right column — rating on desktop. Hidden on phone (rating moves inline above). */}
      <div className="hidden sm:flex shrink-0">
        {recipe.rating != null && <Rating value={recipe.rating} />}
      </div>
    </Link>
  );
}
